import React, { useState, useEffect } from "react";

const CreateLoanModal = ({
  isCreateLoanModalOpen,
  closeCreateLoanModal,
  loanProductName,
  loanLimit,
  loanInterest,
  handleReviewLoanDetails,
}) => {
  const [loanAmount, setLoanAmount] = useState("");
  const [loanDuration, setLoanDuration] = useState("");
  const [repaymentInterval, setRepaymentInterval] = useState("Monthly");
  const [totalRepayment, setTotalRepayment] = useState(0);
  const [monthlyRepayment, setMonthlyRepayment] = useState(0);
  const [repaymentMonths, setRepaymentMonths] = useState(1);
  const [purpose, setPurpose] = useState("");
  const [error, setError] = useState("");

  const loanDurations = {
    "Emergency Loan": ["2 Months", "3 Months", "5 Months", "6 Months"],
    "Normal Loan": Array.from({ length: 23 }, (_, i) => `${i + 2} Months`),
    "Topup Loan": Array.from({ length: 23 }, (_, i) => `${i + 2} Months`),
  };

  useEffect(() => {
    const durations = loanDurations[loanProductName] || [];
    setLoanDuration(durations[0] || "");
    setError("");
  }, [loanProductName]);

  useEffect(() => {
    if (!isCreateLoanModalOpen) {
      setLoanAmount("");
      setLoanDuration(loanDurations[loanProductName]?.[0] || "");
      setError("");
      setPurpose("");
    }
  }, [isCreateLoanModalOpen]);

  // Keep repaymentMonths in sync with the selected duration regardless of
  // whether an amount has been entered yet.
  useEffect(() => {
    if (!loanDuration) return;
    const monthsMatch = loanDuration.match(/\d+/);
    const months = monthsMatch ? parseInt(monthsMatch[0]) : 1;
    setRepaymentMonths(months);
  }, [loanDuration]);

  // Recalculate totals whenever amount, duration, or interest changes.
  useEffect(() => {
    if (!loanAmount || !loanDuration) {
      setTotalRepayment(0);
      setMonthlyRepayment(0);
      return;
    }

    const monthsMatch = loanDuration.match(/\d+/);
    const months = monthsMatch ? parseInt(monthsMatch[0]) : 1;

    const principal = parseFloat(loanAmount.replace(/,/g, "")) || 0;
    const interestRate = loanInterest < 1 ? loanInterest : loanInterest / 100;

    let balance = principal;
    const principalPayment = principal / months;
    let totalAmountPaid = 0;

    for (let month = 1; month <= months; month++) {
      const interest = balance * interestRate;
      let totalPayment = principalPayment + interest;

      if (month === months) {
        totalPayment = balance + interest;
      }

      totalAmountPaid += totalPayment;
      balance = month === months ? 0 : balance - principalPayment;
    }

    setTotalRepayment(totalAmountPaid.toFixed(2));
    setMonthlyRepayment((totalAmountPaid / months).toFixed(2));
  }, [loanAmount, loanDuration, loanInterest]);

  if (!isCreateLoanModalOpen) return null;

  const handleAmountChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, "");
    if (parseFloat(rawValue) > loanLimit) {
      setError(`Max Loan Amount: KSH ${Number(loanLimit).toLocaleString()}`);
      return;
    }
    setError("");
    setLoanAmount(rawValue);
  };

  const handleContinue = () => {
    if (error || !loanAmount) return;

    const normalizedInterest =
      loanInterest < 1 ? loanInterest * 100 : loanInterest;

    handleReviewLoanDetails({
      amount: Number(loanAmount),
      monthlyRepayment: Number(monthlyRepayment),
      duration: loanDuration,
      interest: normalizedInterest,
      repaymentInterval,
      totalRepayment: Number(totalRepayment),
      // purpose,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
      {/* Retained 800px desktop max-width structure with viewport safety bounds */}
      <div className="relative bg-white shadow-xl rounded-md w-full max-w-[800px] my-auto flex flex-col max-h-[90vh] transition-all">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 shrink-0">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 pr-4 break-words">
            Create Loan – <span className="font-bold">{loanProductName}</span>
          </h2>
          <button
            onClick={closeCreateLoanModal}
            className="text-gray-500 hover:text-gray-800 text-xl font-bold leading-none p-1 shrink-0"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <p className="text-gray-600 text-xs sm:text-sm mb-6">
            Provide the loan parameters below to complete the creation process.
          </p>

          {/* Form Fields Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-x-8 sm:gap-y-5 mb-8">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Loan Amount
              </label>
              <div>
                <div className="flex">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-xs sm:text-sm text-gray-600 font-medium">
                      KES
                    </div>
                    <input
                      type="text"
                      className={`block w-full pl-12 pr-3 py-2 border ${
                        error ? "border-red-500" : "border-gray-300"
                      } rounded-md text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-[#074073]`}
                      value={loanAmount}
                      onChange={handleAmountChange}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-red-600 font-medium break-words">
                  {error ||
                    `Max Loan Amount: KSH ${Number(
                      loanLimit,
                    ).toLocaleString()}`}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Select Loan Duration
              </label>
              <select
                className="block w-full pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-[#074073] cursor-pointer"
                value={loanDuration}
                onChange={(e) => setLoanDuration(e.target.value)}
              >
                {(loanDurations[loanProductName] || []).map((duration) => (
                  <option key={duration}>{duration}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Loan Interest (%)
              </label>
              <input
                type="text"
                className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-xs sm:text-sm text-gray-900 cursor-not-allowed"
                value={loanInterest < 1 ? loanInterest * 100 : loanInterest}
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Repayment Interval
              </label>
              <input
                type="text"
                className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-xs sm:text-sm text-gray-900 cursor-not-allowed"
                value={repaymentInterval}
                readOnly
              />
            </div>

            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-800 mb-1">
                Purpose
              </label>
              <input
                type="text"
                className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md bg-white text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-[#074073]"
                placeholder="Enter Loan Purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              />
            </div>
          </div>

          {/* Repayment Summary Box */}
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">
            Repayment Summary
          </h3>

          <div className="bg-[#dcecff] p-4 sm:p-5 rounded-md text-xs sm:text-sm space-y-2">
            <div className="flex justify-between items-center gap-2">
              <span className="text-gray-700">Total Repayment</span>
              <strong className="text-gray-900 font-semibold break-all text-right">
                KES {Number(totalRepayment).toLocaleString()}
              </strong>
            </div>

            <div className="flex justify-between items-center gap-2 pt-1 border-t border-blue-100/50">
              <span className="text-gray-700">Monthly</span>
              <strong className="text-gray-900 font-semibold break-all text-right">
                KES {Number(monthlyRepayment).toLocaleString()} ·{" "}
                {repaymentMonths} Months
              </strong>
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="flex flex-col-reverse sm:flex-row justify-between p-4 sm:p-6 border-t border-gray-200 gap-3 shrink-0">
          <button
            onClick={closeCreateLoanModal}
            className="w-full sm:w-auto border border-[#074073] text-[#074073] hover:bg-[#f5f9ff] font-medium py-2.5 sm:py-2 px-5 rounded-md text-xs sm:text-sm transition-colors duration-150"
          >
            Cancel Application
          </button>

          <button
            onClick={handleContinue}
            disabled={!!error || !loanAmount}
            className={`w-full sm:w-auto font-medium py-2.5 sm:py-2 px-6 rounded-md text-xs sm:text-sm transition-colors duration-150 ${
              error || !loanAmount
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#074073] hover:bg-[#052f5a] text-white"
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLoanModal;
